import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '@fuse/Dialog/confirmation-dialog/confirmation-dialog.component';
import { ToolbarService } from 'app/layout/components/toolbar/toolbar.service';
import { EmailTemplateApiService } from '../email-template-api.service';
import { EmailDesignerComponent } from '../email-designer/email-designer.component';
import { EmailTemplateApiError, SaveRequest, SUBJECT_MAX_LENGTH, TemplateDetail, TEXT_MAX_LENGTH } from '../models';
import { readDesign, writeDesign } from '../utils/design';
import { htmlToText } from '../utils/html-to-text';
import { fillSamples, humanize, placeholder } from '../utils/merge-tags';
import { validateTemplateContent } from '../utils/placeholder-validator';
import { detailStatus, STATUS_LABELS, TemplateStatus } from '../utils/status';

interface PreviewState {
    heading: string;
    subject: string;
    html: string;
    note: string;
}

@Component({
    selector: 'app-template-editor',
    templateUrl: './template-editor.component.html',
    styleUrls: ['./template-editor.component.scss']
})
export class TemplateEditorComponent implements OnInit {

    @ViewChild(EmailDesignerComponent) private designer: EmailDesignerComponent;
    @ViewChild('subjectInput') private subjectInput: ElementRef<HTMLInputElement>;
    @ViewChild('htmlInput') private htmlInput: ElementRef<HTMLTextAreaElement>;
    @ViewChild('textInput') private textInput: ElementRef<HTMLTextAreaElement>;

    readonly SUBJECT_MAX_LENGTH = SUBJECT_MAX_LENGTH;
    readonly TEXT_MAX_LENGTH = TEXT_MAX_LENGTH;

    templateKey: string;
    detail: TemplateDetail | null = null;
    loading = true;
    loadError = '';

    subject = '';
    private originalSubject = '';
    textBody = '';
    private originalText = '';
    autoText = true;

    /** 'html' when the template has no design JSON and is edited as raw HTML. */
    mode: 'design' | 'html' = 'design';
    designSupported = false;
    initialDesign: object | null = null;
    /** Bumped to recreate the visual editor with new content. */
    designerRevision = 0;
    designerReady = false;
    designDirty = false;
    /** Stored HTML; in HTML-only mode also the text being edited. */
    rawHtml = '';
    private originalHtml = '';

    saving = false;
    busy = false;
    errors: string[] = [];
    preview: PreviewState | null = null;
    private leaving = false;

    humanize = humanize;
    placeholder = placeholder;
    uploadImage = (file: File) => this._emailTemplateApi.uploadImage(file);

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _emailTemplateApi: EmailTemplateApiService,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _toolbarService: ToolbarService,
    ) {
        this._toolbarService._ToolBarTitle.next('Email Templates');
        this._toolbarService._isBackBtnShow.next(false);
    }

    ngOnInit(): void {
        this.templateKey = this._route.snapshot.paramMap.get('templateKey');
        this.load();
    }

    get variables(): string[] {
        return this.detail?.templateVariables || [];
    }

    get status(): TemplateStatus {
        return detailStatus(this.detail);
    }

    get statusLabel(): string {
        return STATUS_LABELS[this.status];
    }

    get canEdit(): boolean {
        return !!this.detail?.defaultAvailable;
    }

    get isDirty(): boolean {
        if (!this.detail) {
            return false;
        }
        return this.subject !== this.originalSubject
            || this.designDirty
            || (this.mode === 'html' && this.rawHtml !== this.originalHtml)
            || (!this.autoText && this.textBody !== this.originalText);
    }

    load(): void {
        this.loading = true;
        this.loadError = '';
        this._emailTemplateApi.get(this.templateKey).then((detail) => {
            this.applyDetail(detail, true);
        }, (error: EmailTemplateApiError) => {
            this.loadError = error.status === 403
                ? 'Email templates are only available to the main admin account.'
                : error.message;
        }).finally(() => this.loading = false);
    }

    /**
     * Replaces local state with the server's detail. With reloadContent=false the
     * visual editor keeps its content (it is what was just saved).
     */
    private applyDetail(detail: TemplateDetail, reloadContent: boolean): void {
        this.detail = detail;
        const source = detail.custom || detail.default;
        this.subject = this.originalSubject = source?.emailSubject || '';
        this.textBody = this.originalText = source?.textBody || '';
        this.rawHtml = this.originalHtml = source?.htmlBody || '';
        this.designDirty = false;
        this.errors = [];

        const { supported, design } = readDesign(detail);
        this.designSupported = supported;
        if (reloadContent) {
            this.initialDesign = design;
            this.mode = design ? 'design' : 'html';
            this.remountDesigner();
        }
    }

    private remountDesigner(): void {
        this.designerReady = false;
        this.designerRevision++;
    }

    // ---- Content editing ----

    onDesignChanged(): void {
        this.designDirty = true;
    }

    onImageUploadFailed(error: any): void {
        this._matSnackBar.open(error?.message || 'The image could not be uploaded.', 'OK', { duration: 4000 });
    }

    insertVariable(target: 'subject' | 'html' | 'text', variable: string): void {
        const refs = { subject: this.subjectInput, html: this.htmlInput, text: this.textInput };
        const element = refs[target]?.nativeElement;
        const current: string = target === 'subject' ? this.subject : target === 'html' ? this.rawHtml : this.textBody;
        const tag = placeholder(variable);
        const start = element?.selectionStart ?? current.length;
        const end = element?.selectionEnd ?? start;
        const updated = current.slice(0, start) + tag + current.slice(end);

        if (target === 'subject') {
            this.subject = updated;
        } else if (target === 'html') {
            this.rawHtml = updated;
        } else {
            this.textBody = updated;
        }
        setTimeout(() => {
            element?.focus();
            element?.setSelectionRange(start + tag.length, start + tag.length);
        });
    }

    copyVariable(variable: string): void {
        navigator.clipboard?.writeText(placeholder(variable)).then(
            () => this._matSnackBar.open(`Copied ${placeholder(variable)}`, 'OK', { duration: 1500 }),
            () => { }
        );
    }

    onStartNewDesign(): void {
        this.confirm({
            deleteItem: 'Start a new design?',
            message: 'The current HTML will be replaced by a new design from the visual editor when you save.',
        }).then((confirmed) => {
            if (!confirmed) {
                return;
            }
            this.mode = 'design';
            this.initialDesign = null;
            this.remountDesigner();
            // The admin chose to replace the content, so the empty design counts as a change.
            this.designDirty = true;
        });
    }

    // ---- Save, switch, reset ----

    async onSave(): Promise<void> {
        if (this.saving || this.busy || !this.canEdit) {
            return;
        }
        this.errors = [];
        const request: SaveRequest = { templateKey: this.detail.templateKey };

        if (this.subject !== this.originalSubject) {
            request.emailSubject = this.subject;
        }

        let html: string | undefined;
        let design: object | undefined;
        try {
            if (this.mode === 'design' && this.designDirty) {
                const result = await this.designer.getResult();
                html = result.html;
                design = result.design;
            } else if (this.mode === 'html' && this.rawHtml !== this.originalHtml) {
                html = this.rawHtml;
            }
        } catch (error) {
            this._matSnackBar.open(error?.message || 'The editor is still loading.', 'OK', { duration: 3000 });
            return;
        }

        if (html !== undefined) {
            request.htmlBody = html;
            if (design) {
                writeDesign(request, design, this.designSupported);
            }
            // A stale text version no longer matches the new HTML.
            if (this.autoText) {
                this.textBody = htmlToText(html);
            }
            request.textBody = this.textBody;
        } else if (!this.autoText && this.textBody !== this.originalText) {
            request.textBody = this.textBody;
        }

        // A first save with nothing changed is allowed: it makes a plain copy of the default.
        if (Object.keys(request).length === 1 && this.detail.custom) {
            this._matSnackBar.open('There are no changes to save.', 'OK', { duration: 2000 });
            return;
        }

        this.errors = validateTemplateContent(
            { subject: this.subject, html: request.htmlBody, text: request.textBody },
            this.variables
        );
        if (this.errors.length) {
            return;
        }

        this.saving = true;
        this._emailTemplateApi.save(request).then((result) => {
            this.applyDetail(result.data, false);
            this._matSnackBar.open(result.message || 'Saved.', 'OK', { duration: 2000 });
        }, (error: EmailTemplateApiError) => {
            this.errors = [error.message];
            this._matSnackBar.open(error.message, 'OK', { duration: 4000 });
        }).finally(() => this.saving = false);
    }

    /** Sends only {templateKey, isActive}; unsaved edits stay in the editor. */
    onToggleActive(): void {
        if (!this.detail?.custom || this.busy || this.saving) {
            return;
        }
        this.busy = true;
        const isActive = !this.detail.custom.isActive;
        this._emailTemplateApi.save({ templateKey: this.detail.templateKey, isActive }).then((result) => {
            this.detail = result.data;
            this._matSnackBar.open(result.message || 'Updated.', 'OK', { duration: 2000 });
        }, (error: EmailTemplateApiError) => {
            this._matSnackBar.open(error.message, 'OK', { duration: 4000 });
        }).finally(() => this.busy = false);
    }

    onReset(): void {
        if (!this.detail?.custom || this.busy || this.saving) {
            return;
        }
        this.confirm({
            deleteItem: 'Reset to platform default?',
            message: 'Your version of this email will be deleted and the platform default will be used. This cannot be undone.',
            image: '/assets/ui/delete.png',
        }).then((confirmed) => {
            if (!confirmed) {
                return;
            }
            this.busy = true;
            this._emailTemplateApi.delete(this.detail.templateKey).then((result) => {
                this._matSnackBar.open(result.message || 'Deleted successfully.', 'OK', { duration: 2000 });
                if (result.data) {
                    this.applyDetail(result.data, true);
                } else {
                    this.leaving = true;
                    this._router.navigate(['/email-templates']);
                }
            }, (error: EmailTemplateApiError) => {
                this._matSnackBar.open(error.message, 'OK', { duration: 4000 });
            }).finally(() => this.busy = false);
        });
    }

    onBack(): void {
        this._router.navigate(['/email-templates']);
    }

    // ---- Preview ----

    async onPreview(): Promise<void> {
        let html = this.rawHtml;
        if (this.mode === 'design' && this.designDirty) {
            try {
                html = (await this.designer.getResult()).html;
            } catch (error) {
                this._matSnackBar.open(error?.message || 'The editor is still loading.', 'OK', { duration: 3000 });
                return;
            }
        }
        this.preview = {
            heading: 'Preview',
            subject: fillSamples(this.subject, this.variables),
            html: fillSamples(html, this.variables),
            note: 'Variables are filled with sample values.',
        };
    }

    onViewDefault(): void {
        const def = this.detail?.default;
        if (!def) {
            return;
        }
        this.preview = {
            heading: 'Platform default (read-only)',
            subject: fillSamples(def.emailSubject, this.variables),
            html: fillSamples(def.htmlBody, this.variables),
            note: 'This is the email sent when your version is not used. Variables are filled with sample values.',
        };
    }

    // ---- Unsaved changes ----

    canDeactivate(): boolean | Promise<boolean> {
        if (this.leaving || !this.isDirty) {
            return true;
        }
        return this.confirm({
            deleteItem: 'Leave without saving?',
            message: 'You have unsaved changes to this email template. They will be lost.',
        });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.isDirty) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    private confirm(data: { deleteItem: string; message: string; image?: string }): Promise<boolean> {
        return this._matDialog.open(ConfirmationDialogComponent, {
            panelClass: ['commonDialogWrapper'],
            backdropClass: 'dialogBackdrop',
            disableClose: false,
            data: {
                ...data,
                focus: 'right',
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            }
        }).afterClosed().toPromise().then(result => !!result);
    }
}
